import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Webhook delivery hardening (part 131).
 *
 * Two lists and one action. The deliveries table is the queue as the sender sees it: state,
 * attempt counter, the next time the ladder fires and the last answer from the receiver. The dead
 * letter table is where a delivery ends up when the six-attempt ladder is exhausted or the
 * receiver answers 4xx; "เล่นซ้ำ" sends it once more, with a fresh signature over the stored body,
 * and continues the attempt counter (attempt 7, never attempt 1 again).
 *
 * The page does not compute the ladder or the classification — the API returns the numbers the
 * worker used. Nothing here hides a failure: a replay that fails comes back as state dead and the
 * receiver's answer is shown verbatim.
 *
 * The page is not routed from App.tsx in this part, matching the unmounted API group. Wiring is
 * one import and one <Route>, reviewed together with the worker that drains the queue.
 */

type DeliveryState = 'pending' | 'retrying' | 'delivered' | 'dead'
type DLQState = 'open' | 'replayed' | 'discarded'
type Reason = 'max_attempts' | 'permanent_status' | 'invalid_request'

interface Attempt {
  attempt: number
  at: string
  status_code: number
  outcome: 'delivered' | 'retryable' | 'permanent'
  error?: string
  response_excerpt?: string
  duration_ms: number
  delay_ms?: number
  replay?: boolean
}

interface DeliveryRow {
  id: string
  webhook_id: string
  webhook_url: string
  event: string
  state: DeliveryState
  state_label_th: string
  attempts: number
  status_code: number
  last_error?: string
  next_attempt_at: string
  delivered_at?: string
  created_at: string
  attempt_log: Attempt[]
}

interface DeadLetterRow {
  id: string
  webhook_id: string
  webhook_url: string
  delivery_id: string
  event: string
  attempts: number
  last_status_code: number
  last_error: string
  reason: Reason
  reason_label_th: string
  state: DLQState
  replay_count: number
  last_replayed_at?: string
  first_failed_at: string
  last_attempt_at: string
  created_at: string
}

interface ReplayResult {
  delivered: boolean
  state: DeliveryState
  state_label: string
  attempts: number
  status_code: number
  attempt_log: Attempt[]
}

const DELIVERIES_KEY = ['webhook-dlq', 'deliveries'] as const
const DLQ_KEY = ['webhook-dlq', 'dlq'] as const

const RETRY_LADDER = ['1 นาที', '5 นาที', '30 นาที', '2 ชั่วโมง', '6 ชั่วโมง']

export function WebhookDLQ() {
  const qc = useQueryClient()
  const { me } = useAuth()
  const canReplay = me?.role === 'owner' || me?.role === 'admin'

  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [onlyOpen, setOnlyOpen] = useState(true)

  const deliveries = useQuery({
    queryKey: [...DELIVERIES_KEY, 'all'],
    queryFn: () => api<DeliveryRow[]>('/v1/webhooks/deliveries?limit=100'),
    refetchInterval: 15_000,
  })

  const deadLetters = useQuery({
    queryKey: [...DLQ_KEY, onlyOpen ? 'open' : 'all'],
    queryFn: () => api<DeadLetterRow[]>(`/v1/webhooks/dlq?limit=100${onlyOpen ? '&state=open' : ''}`),
    refetchInterval: 15_000,
  })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: DELIVERIES_KEY })
    void qc.invalidateQueries({ queryKey: DLQ_KEY })
  }

  const replay = useMutation({
    mutationFn: (id: string) => apiWrite<ReplayResult>(`/v1/webhooks/dlq/${id}/replay`, 'POST', {}),
    onSuccess: (result) => {
      setError('')
      setNotice(
        result.delivered
          ? `ส่งซ้ำสำเร็จในการพยายามครั้งที่ ${result.attempts} (HTTP ${result.status_code})`
          : `ส่งซ้ำไม่สำเร็จในการพยายามครั้งที่ ${result.attempts} รายการกลับเข้าคิวอีกครั้ง`,
      )
      refresh()
    },
    onError: (err) => {
      setNotice('')
      setError(errorText(err, 'ส่งซ้ำไม่สำเร็จ'))
    },
  })

  const rows = deliveries.data ?? []
  const queue = rows.filter((row) => row.state === 'pending' || row.state === 'retrying')
  const delivered = rows.filter((row) => row.state === 'delivered')
  const dead = rows.filter((row) => row.state === 'dead')
  const open = deadLetters.data ?? []

  return (
    <>
      <PageHead
        title="คิวส่ง webhook และ dead letter"
        sub="ตรวจสอบการลองใหม่ตามลำดับ 1 นาที 5 นาที 30 นาที 2 ชั่วโมง 6 ชั่วโมง และส่งซ้ำรายการที่ล้มเหลวถาวร"
        actions={
          <button className="btn btn-ghost" onClick={refresh} disabled={deliveries.isFetching || deadLetters.isFetching}>
            {deliveries.isFetching || deadLetters.isFetching ? 'กำลังอ่าน…' : 'อ่านข้อมูลใหม่'}
          </button>
        }
      />

      {error ? (
        <div className="card card-pad" style={{ borderColor: 'var(--color-critical)' }}>
          <span className="sev sev-critical">
            <i className="sev-dot" />
            {error}
          </span>
        </div>
      ) : null}

      {notice ? (
        <div className="card card-pad" style={{ borderColor: 'var(--color-emerald)' }}>
          <span className="sev sev-low">
            <i className="sev-dot" />
            {notice}
          </span>
        </div>
      ) : null}

      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat label="งานในคิว" value={number(queue.length)} unit="รายการ" foot="สถานะรอส่งหรือกำลังลองใหม่" />
        <Stat label="ส่งสำเร็จ" value={number(delivered.length)} unit="รายการ" foot="ครั้งล่าสุดได้รับ 2xx" />
        <Stat label="ส่งไม่สำเร็จ" value={number(dead.length)} unit="รายการ" foot="ลองครบหรือถูกปฏิเสธถาวร" />
        <Stat label="dead letter ที่เปิดอยู่" value={number(open.length)} unit="รายการ" foot="เล่นซ้ำได้โดยผู้ดูแล" />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-pad" style={{ paddingBottom: 8 }}>
          <div className="row">
            <span className="card-label">คิวส่งล่าสุด</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
              ลำดับการลองใหม่: {RETRY_LADDER.join(' · ')} แล้วหยุดที่ความพยายามครั้งที่ 6
            </span>
          </div>
        </div>
        {deliveries.isLoading ? (
          <div className="card-pad stack" style={{ gap: 8 }}>
            <Skel h={14} />
            <Skel h={14} w="70%" />
          </div>
        ) : rows.length === 0 ? (
          <div className="card-pad">
            <Empty title="ยังไม่มีงานส่ง" body="เมื่อมีเหตุการณ์ที่ webhook สมัครไว้ ระบบจะสร้างงานส่งและแสดงสถานะที่นี่" />
          </div>
        ) : (
          <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>เหตุการณ์</th>
                  <th>ปลายทาง</th>
                  <th>สถานะ</th>
                  <th>ความพยายาม</th>
                  <th>ตอบกลับล่าสุด</th>
                  <th>ครั้งถัดไป</th>
                  <th>ข้อผิดพลาดล่าสุด</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{row.event}</div>
                      <div className="mono dim" style={{ fontSize: 11.5 }}>{row.id}</div>
                    </td>
                    <td className="mono dim" style={{ maxWidth: 260, overflowWrap: 'anywhere' }}>{row.webhook_url || 'ไม่พบปลายทาง'}</td>
                    <td>
                      <span className={`sev sev-${toneFor(row.state)}`}>
                        <i className="sev-dot" />
                        {row.state_label_th || row.state}
                      </span>
                    </td>
                    <td className="mono">
                      {row.attempts} / 6
                      {row.attempt_log.length > 0 ? (
                        <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                          {row.attempt_log
                            .slice(-3)
                            .map((a) => `${a.attempt}:${a.status_code || 'ไม่ตอบ'}`)
                            .join(' · ')}
                        </div>
                      ) : null}
                    </td>
                    <td className="mono">{row.status_code ? `HTTP ${row.status_code}` : 'ยังไม่ได้รับคำตอบ'}</td>
                    <td className="dim">
                      {row.state === 'delivered' && row.delivered_at
                        ? `ส่งเมื่อ ${thaiDateTime(row.delivered_at)}`
                        : row.state === 'dead'
                          ? 'หยุดแล้ว'
                          : thaiDateTime(row.next_attempt_at)}
                    </td>
                    <td className="dim" style={{ maxWidth: 320, overflowWrap: 'anywhere' }}>{row.last_error || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-pad" style={{ paddingBottom: 8 }}>
          <div className="row">
            <span className="card-label">dead letter</span>
            <label className="row dim" style={{ marginLeft: 'auto', gap: 6, fontSize: 11.5 }}>
              <input type="checkbox" checked={onlyOpen} onChange={(event) => setOnlyOpen(event.target.checked)} />
              แสดงเฉพาะรายการที่เปิดอยู่
            </label>
          </div>
        </div>
        {deadLetters.isLoading ? (
          <div className="card-pad stack" style={{ gap: 8 }}>
            <Skel h={14} />
            <Skel h={14} w="60%" />
          </div>
        ) : open.length === 0 ? (
          <div className="card-pad">
            <Empty
              title={onlyOpen ? 'ไม่มี dead letter ที่เปิดอยู่' : 'ยังไม่มี dead letter'}
              body="รายการจะมาที่นี่เมื่อลองครบทั้ง 6 ครั้ง หรือเมื่อปลายทางตอบสถานะที่การลองใหม่ไม่ได้ช่วย"
            />
          </div>
        ) : (
          <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>เหตุการณ์</th>
                  <th>ปลายทาง</th>
                  <th>สาเหตุ</th>
                  <th>ความพยายาม</th>
                  <th>คำตอบล่าสุด</th>
                  <th>เล่นซ้ำแล้ว</th>
                  <th>การดำเนินการ</th>
                </tr>
              </thead>
              <tbody>
                {open.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{row.event}</div>
                      <div className="mono dim" style={{ fontSize: 11.5 }}>{row.delivery_id}</div>
                    </td>
                    <td className="mono dim" style={{ maxWidth: 240, overflowWrap: 'anywhere' }}>{row.webhook_url || 'ไม่พบปลายทาง'}</td>
                    <td>
                      <span className={`sev sev-${row.reason === 'max_attempts' ? 'medium' : 'high'}`}>
                        <i className="sev-dot" />
                        {row.reason_label_th || row.reason}
                      </span>
                      <div className="dim" style={{ fontSize: 11.5, marginTop: 4, maxWidth: 320, overflowWrap: 'anywhere' }}>
                        {row.last_error}
                      </div>
                    </td>
                    <td className="mono">{row.attempts} ครั้ง</td>
                    <td className="mono">{row.last_status_code ? `HTTP ${row.last_status_code}` : 'ไม่ได้รับคำตอบ'}</td>
                    <td className="dim">
                      {row.replay_count > 0
                        ? `${row.replay_count} ครั้ง${row.last_replayed_at ? ` · ${thaiDateTime(row.last_replayed_at)}` : ''}`
                        : 'ยังไม่เคย'}
                    </td>
                    <td>
                      {row.state === 'open' ? (
                        <button
                          className="btn btn-sm"
                          disabled={!canReplay || replay.isPending}
                          title={canReplay ? 'ส่งอีกครั้งด้วยลายเซ็นใหม่และนับความพยายามต่อ' : 'ต้องเป็นผู้ดูแลหรือเจ้าขององค์กร'}
                          onClick={() => replay.mutate(row.id)}
                        >
                          {replay.isPending && replay.variables === row.id ? 'กำลังส่งซ้ำ…' : 'เล่นซ้ำ'}
                        </button>
                      ) : (
                        <span className="dim">เล่นซ้ำแล้ว</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

function toneFor(state: DeliveryState): string {
  switch (state) {
    case 'delivered':
      return 'low'
    case 'dead':
      return 'critical'
    case 'retrying':
    case 'pending':
      return 'medium'
    default:
      return 'low'
  }
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}
